const userManagement = {
  moduleTitle: "Users",
  nav: {
    drivers: "Drivers",
  },
  actions: {
    view: "View",
    create: "Create",
    update: "Update",
    delete: "Delete",
  },
  // NavDrawer.tsx swaps to this key at render time for userType "staff",
  // a presentation-only override that never touches the static nav tree.
  moduleTitleStaff: "Driver Management",
  permissions: {
    core: "Core",
    userManagement: "User Management",
    users: "Users",
    drivers: "Drivers",
  },
  drivers: {
    title: "Drivers",
    stats: {
      label: "Driver statistics",
      total: "Total drivers",
      assigned: "Assigned drivers",
      unassigned: "Unassigned drivers",
      expiredLicenses: "Expired licenses",
      loading: "Loading",
      error: "Unavailable",
    },
    toolbar: {
      search: "Search...",
      searchLabel:
        "Search by driver name, national ID / passport or license number",
      filters: "Filters",
      add: "Add driver",
      archive: "Archive",
    },
    create: {
      breadcrumb: "Add driver",
      title: "Add driver",
      subtitle:
        "Enter the new driver's details and upload the required documents.",
      back: "Back to drivers",
      tabs: {
        basicInfo: "Basic information",
        attachments: "Attachments",
      },
      sections: {
        personal: "Personal information",
        work: "Work information",
        license: "Driving license information",
      },
      photo: {
        label: "Profile picture",
        upload: "Upload photo",
        remove: "Remove photo",
        hint: "JPG or PNG, max 5MB",
        invalidType: "The profile picture must be a JPG or PNG image.",
        tooLarge: "The profile picture must not exceed 5MB.",
      },
      fields: {
        optional: "(optional)",
        company: "Company",
        companyPlaceholder: "Select company",
        companySearch: "Search companies",
        companyEmpty: "No companies found",
        branch: "Branch",
        branchPlaceholder: "Select branch",
        branchSearch: "Search branches",
        branchEmpty: "No branches found",
        loading: "Loading",
        nameAr: "Arabic name",
        nameArPlaceholder: "e.g. احمد محمد احمد",
        nameEn: "English name",
        nameEnPlaceholder: "e.g. AHMED MOHAMED AHMED",
        nationalId: "National ID / Passport number",
        username: "Username",
        email: "Email",
        rfid: "RFID",
        mobileNumber1: "Phone number",
        mobileNumber2: "Phone number",
        countrySelect: "Country code",
        licenseNumber: "License number",
        licenseIssueDate: "Issue date",
        licenseExpiryDate: "License expiry date",
        datePlaceholder: "MM/DD/YYYY",
        nextMonth: "Next month",
        previousMonth: "Previous month",
      },
      countries: {
        ksa: "Saudi Arabia",
        egypt: "Egypt",
        uae: "United Arab Emirates",
        jordan: "Jordan",
      },
      validation: {
        required: "This field is required.",
        minLength: "This field must contain at least 4 characters.",
        maxLength: "This field must not exceed 50 characters.",
        arabicOnly: "Name must contain Arabic letters only.",
        englishOnly: "Name must contain English letters only.",
        usernameFormat:
          "Username must start with a letter and contain only English letters, numbers, dots, underscores or hyphens.",
        emailInvalid: "Please enter a valid email address.",
        emailLength: "Email must be between 6 and 254 characters.",
        phoneInvalid:
          "Please enter a valid phone number for the selected country.",
        phoneLeadingZero:
          "Egyptian phone numbers must start with 0 (e.g. 01012345678).",
        phoneEgyptLength: "Egyptian phone numbers must be exactly 11 digits.",
        phoneSaudiStart:
          "Saudi phone numbers must start with 5 (e.g. 512345678).",
        phoneSaudiLength: "Saudi phone numbers must be exactly 9 digits.",
        phoneLength:
          "Phone number must contain a valid number of digits for the selected country.",
        minWords: "Enter at least three words (first, father and family name).",
        expiryInPast: "The license expiry date must be a future date.",
        expiryBeforeIssue: "The expiry date must be after the issue date.",
      },
      duplicate: {
        username: "User already exists.",
        email: "Email already exists.",
        nationalId: "National ID already exists.",
      },
      attachments: {
        input: "Upload documents",
        dropzoneTitle: "Drag files here or click to browse",
        dropzoneHint: "PDF, PNG, JPG, up to 25MB per file, 10 files max",
        browse: "Browse files",
        addMore: "Add more files",
        remove: "Remove {{name}}",
        pending: "Pending upload",
        invalidType: "Only PDF, PNG and JPG files are allowed.",
        tooLarge: "Each file must not exceed 25MB.",
        tooMany: "You can upload up to 10 files.",
      },
      cancel: "Cancel",
      save: "Save",
      success:
        "Driver created. An activation email has been sent to the driver.",
      failure: "The driver could not be created.",
      restoreDialog: {
        title: "Restore Driver",
        question:
          "A driver with the same information already exists in the archived drivers.",
        prompt: "Would you like to restore this driver?",
        cancel: "Cancel",
        confirm: "Restore Driver",
        close: "Close restore dialog",
        success: "Driver restored successfully.",
        failure: "Unable to restore the driver. Please try again.",
      },
      unsavedChanges: {
        title: "Unsaved changes",
        message: "You have unsaved changes. Leave without saving?",
        stay: "Stay",
        leave: "Leave",
        close: "Close",
      },
    },
    edit: {
      back: "Back to drivers",
      save: "Save changes",
      success: "Driver updated successfully.",
      failure: "The driver could not be updated.",
      notAvailable: "N/A",
      fields: {
        status: "Status",
        active: "Active",
        inactive: "Inactive",
      },
      photo: {
        change: "Change photo",
      },
      attachments: {
        existing: "Uploaded documents",
        view: "View {{name}}",
      },
      statusDialog: {
        activateTitle: "Activate driver",
        deactivateTitle: "Deactivate driver",
        activate: "Are you sure you want to activate this Driver?",
        deactivate: "Are you sure you want to deactivate this Driver?",
        note: "This change won't be applied until you save.",
        yes: "Yes",
        no: "No",
        close: "Close",
      },
    },
    resendActivation: {
      action: "Resend activation link",
      title: "Resend activation link",
      message:
        "A new activation link will be emailed to this driver. Do you want to continue?",
      messageTo:
        "A new activation link will be emailed to {{email}}. Do you want to continue?",
      cancel: "Cancel",
      confirm: "Resend",
      close: "Close resend dialog",
      success: "The activation link was sent to the driver.",
      failure: "The activation link could not be sent. Please try again.",
    },
    filters: {
      status: "Status",
      allStatuses: "All statuses",
      expiryDate: "License expiry date",
      expiryDatePlaceholder: "Select a date range",
      clearDateRange: "Clear date range",
      reset: "Reset filters",
    },
    datePicker: {
      nextMonth: "Next month",
      previousMonth: "Previous month",
    },
    table: {
      name: "Driver name",
      company: "Company",
      nationalId: "National ID / Passport number",
      licenseNumber: "License number",
      licenseExpiryDate: "License expiry date",
      status: "Status",
      expired: "Expired",
      error: "Drivers could not be loaded.",
      loading: "Loading drivers",
      showingCount: "Showing {{count}} of {{total}}",
      rowsPerPageLabel: "Rows per page",
      nextPage: "Next page",
      previousPage: "Previous page",
      pagination: "Pagination",
      sortAscending: "Sort ascending",
      sortDescending: "Sort descending",
    },
    empty: {
      noDrivers: "No Drivers have been created yet.",
      noSearchResults: "No Drivers found matching your search.",
      noFilterResults: "No Driver found matching the selected filters.",
      clearSearch: "Clear search",
    },
    status: {
      active: "Active",
      inactive: "Inactive",
      pendingActivation: "Pending activation",
    },
    actions: {
      trigger: "Driver actions",
      view: "View",
      edit: "Update",
      delete: "Delete",
    },
    deleteDialog: {
      title: "Delete",
      question: "Are you sure you want to delete this driver?",
      warning: "The driver will move to the archive and can be restored later.",
      confirmWord: "Delete",
      instruction: 'Type "{{word}}" to confirm deletion',
      inputLabel: "Deletion confirmation",
      cancel: "Cancel",
      confirm: "Delete",
      close: "Close delete dialog",
      success: "Driver deleted and moved to the archive.",
    },
    archived: {
      title: "Archived drivers",
      subtitle: "Drivers that were archived and can be restored.",
      back: "Back to drivers",
      search: "Search...",
      searchLabel: "Search by name, username or email",
      filters: "Filters",
      archivedAt: "Archive date",
      archivedAtPlaceholder: "Select a date range",
      clearDateRange: "Clear date range",
      nextMonth: "Next month",
      previousMonth: "Previous month",
      empty: "No archived drivers found.",
      restore: "Restore {{name}}",
      table: {
        name: "Driver name",
        username: "Username",
        company: "Company",
        email: "Email",
        archivedAt: "Archive date",
        error: "Archived drivers could not be loaded.",
        loading: "Loading archived drivers",
      },
      restoreDialog: {
        title: "Restore Driver",
        question: "Are you sure you want to restore this driver?",
        prompt:
          "The driver will return to the drivers list with the status they had before.",
        cancel: "Cancel",
        confirm: "Restore",
        close: "Close restore dialog",
        success: "Driver restored successfully.",
        failure: "Unable to restore the driver. Please try again.",
      },
    },
    detail: {
      back: "Back to drivers",
      edit: "Edit",
      loading: "Loading driver details",
      error: "Driver details could not be loaded.",
      notFound: "Driver not found.",
      notAvailable: "N/A",
      tabs: {
        basicInfo: "Basic information",
        attachments: "Attachments",
      },
      profile: "Personal profile",
      accountStatus: "Account status",
      nationalId: "National ID / Passport number",
      mobileNumber1: "Mobile number 1",
      mobileNumber2: "Mobile number 2",
      email: "Email",
      rfid: "RFID tag",
      license: "Driving license information",
      licenseNumber: "License number",
      issueDate: "Issue date",
      expiryDate: "Expiry date",
      expired: "Expired",
      company: "Company",
      work: "Work information",
      branch: "Branch",
    },
    attachments: {
      empty: "No documents have been uploaded.",
      uploadedAt: "Uploaded {{date}}",
      view: "View {{name}}",
      download: "Download {{name}}",
    },
  },
  users: {
    title: "User Management",
    subtitle:
      "Manage operational user accounts and link them to departments and branches",
    table: {
      name: "Name",
      username: "Username",
      email: "Email",
      branch: "Branch",
      department: "Department",
      status: "Status",
      error: "Users could not be loaded.",
      loading: "Loading users",
      showingCount: "Showing {{count}} of {{total}}",
      rowsPerPageLabel: "Rows per page",
      nextPage: "Next page",
      previousPage: "Previous page",
      pagination: "Pagination",
      sortAscending: "Sort ascending",
      sortDescending: "Sort descending",
    },
    toolbar: {
      searchLabel: "Search by name, username or email",
      search: "Search...",
      filters: "Filters",
      archive: "Archive",
      add: "Add user",
    },
    filters: {
      status: "Status",
      allStatuses: "All statuses",
      reset: "Reset",
    },
    status: {
      active: "Active",
      inactive: "Inactive",
      pendingActivation: "Pending activation",
    },
    empty: {
      noSearchResults: "No results match your search.",
      clearSearch: "Clear search",
      noFilterResults: "No results match these filters.",
      noUsers: "No users yet.",
    },
    actions: {
      trigger: "User actions",
      view: "View",
      edit: "Edit",
      delete: "Delete",
      resendActivation: "Resend activation",
    },
    deleteDialog: {
      title: "Delete",
      question: "Are you sure you want to delete this user?",
      warning: "The user will move to the archive and can be restored later.",
      confirmWord: "Delete",
      instruction: 'Type "{{word}}" to confirm deletion',
      inputLabel: "Deletion confirmation",
      cancel: "Cancel",
      confirm: "Delete",
      close: "Close delete dialog",
      success: "User deleted and moved to the archive.",
    },
    resendActivationDialog: {
      title: "Resend the activation link?",
      message: "A new activation link will be sent to the user's email.",
      confirm: "Resend",
      cancel: "Cancel",
      close: "Close",
    },
    form: {
      sections: {
        basicInfo: "Basic information",
      },
      fields: {
        optional: "(optional)",
        nameAr: "Arabic name",
        nameArPlaceholder: "Full name",
        nameEn: "English name",
        nameEnPlaceholder: "Full Name",
        email: "Email",
        emailPlaceholder: "e.g. a.hassan@tracking.sa",
        username: "Username (login)",
        usernamePlaceholder: "e.g. a.hassan",
        mobileNumber1: "Phone number",
        countrySelect: "Country code",
        department: "Department",
        departmentPlaceholder: "Select department",
        departmentSearch: "Search for a department",
        departmentEmpty: "No departments",
        branch: "Branch",
        branchPlaceholder: "Select",
        branchSearch: "Search for a branch",
        branchEmpty: "No branches",
        loading: "Loading",
        role: "Permissions",
        rolePlaceholder: "Select",
        isActive: "Active",
      },
      countries: {
        ksa: "Saudi Arabia",
        egypt: "Egypt",
        uae: "United Arab Emirates",
        jordan: "Jordan",
      },
      statusDialog: {
        titleActivate: "Activate User",
        titleDeactivate: "Deactivate User",
        questionActivate: "Are you sure you want to activate this user?",
        questionDeactivate: "Are you sure you want to deactivate this user?",
        note: "This change won't be applied until you save.",
        yes: "Yes",
        no: "No",
        close: "Close status change dialog",
      },
      cancel: "Cancel",
    },
    create: {
      breadcrumb: "Add user",
      title: "Add user",
      subtitle: "Enter the new user's information",
      back: "Back to user management",
      save: "Save",
      success: "User created and an activation email was sent.",
      failure: "The user could not be created.",
      duplicate: {
        username: "This username already exists.",
        email: "This email already exists.",
      },
      restoreDialog: {
        title: "Restore user",
        question: "An archived user matches this information.",
        prompt: "Do you want to restore this user?",
        cancel: "Cancel",
        confirm: "Restore user",
        close: "Close restore dialog",
        success: "User restored successfully.",
        failure: "The user could not be restored. Please try again.",
      },
    },
    edit: {
      loading: "Loading user data",
      notAvailable: "This user could not be found.",
      back: "Back to user details",
      save: "Save changes",
      success: "User updated successfully.",
      failure: "The user could not be updated.",
    },
    detail: {
      loading: "Loading user data",
      error: "The user could not be loaded.",
      notAvailable: "Not available",
      back: "Back to user management",
      subtitle: "The user's information and permissions",
      edit: "Edit",
      section: "User details",
      name: "Name",
      username: "Username",
      email: "Email",
      mobileNumber1: "Phone number",
      branch: "Branch",
      department: "Department",
    },
    archived: {
      title: "Archived Users",
      subtitle: "Users who have been archived and can be restored.",
      back: "Back to user management",
      searchLabel: "Search by name, username or email",
      search: "Search...",
      filters: "Filters",
      archivedAt: "Archived at",
      archivedAtPlaceholder: "Select a date range",
      clearDateRange: "Clear date range",
      nextMonth: "Next month",
      previousMonth: "Previous month",
      empty: "No archived users found.",
      restore: "Restore {{name}}",
      table: {
        name: "User name",
        username: "Username",
        email: "Email",
        archivedAt: "Archived at",
        error: "Archived users could not be loaded.",
        loading: "Loading archived users",
      },
      restoreDialog: {
        title: "Restore user",
        question: "Are you sure you want to restore this user?",
        prompt: "The user will return to the list in the same state as before.",
        cancel: "Cancel",
        confirm: "Restore",
        close: "Close restore dialog",
        success: "User restored successfully.",
        failure: "The user could not be restored. Please try again.",
      },
    },
  },
};

export default userManagement;
